import moment from "moment";
import { useEffect, useState } from "react";
import {
  calculateAddHHmmArrayToMoment,
  computeAgendaStatuses,
  getDuration,
  getEndtime,
  getPassedMinutesOnlyTimeSeconds,
  getStarttime,
} from "./timeCalculations";
import { CreateParamsJson } from "./pathHelper";
import { planClock } from "./liveControls";
import { translate } from "../i18n/messages";

function useCounter(ms) {
  const [actualtime, setActualtime] = useState(moment());

  useEffect(() => {
    const interval = setInterval(() => setActualtime(moment()), ms);
    return () => clearInterval(interval);
  }, [ms]);

  return actualtime;
}

// Short plans (an egg timer, a Tabata block) keep their seconds; longer
// ones are rounded to minutes.
export function formatTotalDuration(totalSeconds, language = "en") {
  const t = (key, params) => translate(language, `duration.${key}`, params);
  if (totalSeconds < 10 * 60 && totalSeconds % 60) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return minutes
      ? t("minutesSeconds", { minutes, seconds })
      : t("seconds", { seconds });
  }
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes > 0
      ? t("hoursMinutes", { hours, minutes })
      : t("hours", { hours });
  }
  return t("minutes", { minutes: totalMinutes });
}

// Shared by the compact ring (Progress.js, incl. its native-Fullscreen-API
// toggle) and the standalone full-page timer view (FullscreenTimer.js),
// so both read the same starttime/agenda from the URL and stay in sync.
export default function useTimerData(language = "en") {
  const json = CreateParamsJson();
  const starttime = json.starttime ? moment(json.starttime) : undefined;
  // While paused, the plan is measured against the pause moment instead.
  const actualtime = planClock(json, useCounter(1000));
  const isPaused = Boolean(json.pausedat);
  const agenda = json.agenda ? JSON.parse(json.agenda) : [];

  let isOver = false;
  let value = 0;
  let maxValue = 1;
  let timeLeft = "--:--:--";
  let totalDuration = null;
  let miniAgenda = [];
  let activeCard = null;

  const isEmpty = !starttime || agenda.length === 0;

  if (!isEmpty) {
    const endtime = calculateAddHHmmArrayToMoment(
      starttime,
      agenda.map((item) => item.time)
    );
    isOver = endtime.isBefore(actualtime);
    totalDuration = formatTotalDuration(
      moment.duration(endtime.diff(starttime)).asSeconds(),
      language
    );

    const statuses = computeAgendaStatuses(agenda, starttime, actualtime);
    miniAgenda = agenda.map((task, index) => {
      const array = agenda.slice(0, index + 1);
      return {
        task,
        status: statuses[index],
        start: getStarttime(array, starttime),
        end: getEndtime(array, starttime),
      };
    });

    const activeIndex = statuses.indexOf("active");
    if (activeIndex !== -1) {
      const activeEntry = miniAgenda[activeIndex];
      activeCard = {
        index: activeIndex,
        durationSeconds: moment
          .duration(activeEntry.end.diff(activeEntry.start))
          .asSeconds(),
        remainingSeconds: moment
          .duration(activeEntry.end.diff(actualtime))
          .asSeconds(),
      };
    }

    if (isOver) {
      value = 1;
      maxValue = 1;
      timeLeft = "00:00:00";
    } else {
      maxValue = getDuration(starttime, endtime);
      const passedTime = getPassedMinutesOnlyTimeSeconds(
        starttime,
        actualtime
      );
      value = passedTime;
      timeLeft = moment
        .utc(moment.duration(maxValue - passedTime, "seconds").asMilliseconds())
        .format("HH:mm:ss");
    }
  }

  return {
    starttime,
    agenda,
    isEmpty,
    isOver,
    value,
    maxValue,
    timeLeft,
    totalDuration,
    miniAgenda,
    activeCard,
    isPaused,
    now: actualtime,
  };
}
